import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import {
  Alert,
  AutoComplete,
  Button,
  DatePicker,
  Descriptions,
  Divider,
  Drawer,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
} from 'antd';
import { DeleteOutlined, MinusCircleOutlined, PlusOutlined, QrcodeOutlined } from '@ant-design/icons';
import { API } from '@/services/api.service';
import { CALC_KINDS, STATUS_COLORS, date, errorText, money } from './format';

const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);

/** Form values from a saved calculation; the form works with the net per month. */
const toForm = (c) => ({
  kind: c.kind,
  subject: c.subject,
  project: c.project?._id || null,
  month: c.period.month,
  year: c.period.year,
  months: c.months,
  currency: c.currency,
  rateDate: c.rateDate ? dayjs(c.rateDate) : null,
  exchangeRate: c.exchangeRate,
  payerAccount: c.payerAccount,
  netPurpose: c.netPurpose,
  note: c.note,
  recipients: c.recipients.map((r) => ({
    name: r.name,
    idNumber: r.idNumber,
    address: r.address,
    account: r.account,
    netPerMonth: Math.round((r.net / (c.months || 1)) * 100) / 100,
  })),
});

const fromForm = (v) => ({
  kind: v.kind,
  subject: v.subject,
  project: v.project || null,
  period: { month: v.month, year: v.year },
  months: v.months,
  currency: v.currency,
  rateDate: v.rateDate ? v.rateDate.format('YYYY-MM-DD') : null,
  exchangeRate: v.currency === 'RSD' ? 1 : v.exchangeRate,
  payerAccount: v.payerAccount || null,
  netPurpose: v.kind === 'rent' ? v.netPurpose || null : null,
  note: v.note || null,
  recipients: (v.recipients || []).map((r) => ({ ...r, account: v.kind === 'rent' ? r.account || null : null })),
});

/**
 * One withholding tax calculation (rent or other income). HLB's accountants
 * enter it, save it as a draft to see the figures, file the return in
 * ePorezi from them and type back the BOP and UID; then it is done and its
 * payment orders, with QR codes, carry the BOP. Clients see it read-only.
 *
 * @param {string|null} id - the calculation, or null for a new one
 * @param {object|null} template - a calculation to start a new one from (the next month of the same flat)
 */
export default function CalculationDrawer({
  open,
  company,
  id,
  template,
  projects,
  payerAccounts = [],
  subjects = [],
  latestBySubject = {},
  canManage,
  onClose,
  onSaved,
  onSlip,
  onOpenReturn,
}) {
  const { t } = useTranslation('taxReturns');
  const [form] = Form.useForm();
  const [doneForm] = Form.useForm();
  const [currentId, setCurrentId] = useState(id);
  const kind = Form.useWatch('kind', form);
  const currency = Form.useWatch('currency', form);

  useEffect(() => setCurrentId(id), [id, open]);

  const { data: c, refetch } = useQuery({
    queryKey: ['tax-calculation', company, currentId],
    queryFn: () => API.taxReturns.getCalculation(company, currentId),
    enabled: Boolean(open && currentId),
  });

  const loadRate = async (cur, day) => {
    if (!cur || cur === 'RSD' || !day) return;
    try {
      const { rate } = await API.taxReturns.getExchangeRate(cur, day.format('YYYY-MM-DD'));
      if (rate) form.setFieldValue('exchangeRate', rate);
    } catch {
      /* the accountant types it in */
    }
  };

  // A new calculation starts empty, or from the template with the next month; a saved one from its data.
  useEffect(() => {
    if (!open) return;
    if (currentId && c) {
      form.setFieldsValue(toForm(c));
      doneForm.setFieldsValue({ bop: c.bop, returnNumber: c.returnNumber });
      return;
    }
    if (!currentId) {
      const now = dayjs();
      const base = template ? toForm(template) : null;
      const next = template ? dayjs(`${template.period.year}-${template.period.month}-01`).add(1, 'month') : now;
      form.resetFields();
      form.setFieldsValue({
        kind: 'rent',
        currency: 'EUR',
        months: 1,
        recipients: [{}],
        ...base,
        month: next.month() + 1,
        year: next.year(),
        rateDate: now,
        netPurpose: null,
        note: null,
      });
      doneForm.resetFields();
      loadRate(base?.currency || 'EUR', now);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, currentId, c, template]);

  const saved = (data) => {
    setCurrentId(data._id);
    refetch();
    onSaved?.();
  };

  const save = useMutation({
    mutationFn: (values) =>
      currentId
        ? API.taxReturns.updateCalculation(company, currentId, fromForm(values))
        : API.taxReturns.createCalculation(company, fromForm(values)),
    onSuccess: saved,
  });

  const done = useMutation({
    mutationFn: (values) => API.taxReturns.setCalculationDone(company, currentId, values),
    onSuccess: saved,
  });

  const remove = useMutation({
    mutationFn: () => API.taxReturns.deleteCalculation(company, currentId),
    onSuccess: () => {
      onSaved?.();
      onClose();
    },
  });

  const isDone = c?.status === 'done';
  const editable = canManage && !isDone;
  const kindLabel = (k) => t(`calc.kinds.${k}`);
  const statusLabel = (s) => t(`status.${s}`, { context: canManage ? undefined : 'client' });

  // Picking a flat used before fills in its landlord, amounts and accounts from the last calculation.
  const pickSubject = (value) => {
    const last = latestBySubject[value];
    if (!last || currentId) return;
    const values = toForm(last);
    // The period and the rate date stay as picked for the new month.
    ['month', 'year', 'rateDate'].forEach((k) => delete values[k]);
    form.setFieldsValue(values);
  };

  const results = c && (
    <>
      <Descriptions size="small" column={2} bordered>
        <Descriptions.Item label={t('calc.fields.kind')}>{kindLabel(c.kind)}</Descriptions.Item>
        <Descriptions.Item label={t('fields.project')}>{c.project ? `${c.project.code} ${c.project.name}` : '—'}</Descriptions.Item>
        <Descriptions.Item label={t('calc.fields.subject')} span={2}>
          {c.subject}
        </Descriptions.Item>
        <Descriptions.Item label={t('fields.purpose')}>{c.pid}</Descriptions.Item>
        <Descriptions.Item label={t('calc.fields.months')}>{c.months}</Descriptions.Item>
        {c.currency !== 'RSD' && (
          <Descriptions.Item label={t('calc.fields.exchangeRate')} span={2}>
            {c.exchangeRate} ({c.currency}, {date(c.rateDate)})
          </Descriptions.Item>
        )}
        <Descriptions.Item label={t('calc.fields.payerAccount')} span={2}>
          {c.payerAccount || '—'}
        </Descriptions.Item>
      </Descriptions>
      <Table
        size="small"
        rowKey={(r, i) => `${r.name}-${i}`}
        pagination={false}
        dataSource={c.recipients}
        scroll={{ x: true }}
        columns={[
          { title: t('calc.fields.recipient'), dataIndex: 'name' },
          c.currency !== 'RSD' && {
            title: `${t('calc.fields.net')} ${c.currency}`,
            dataIndex: 'net',
            align: 'right',
            render: money,
          },
          { title: t('calc.fields.netRsd'), dataIndex: 'netRsd', align: 'right', render: money },
          { title: t('calc.fields.gross'), dataIndex: 'gross', align: 'right', render: money },
          {
            title: t('calc.fields.standardizedCosts', { rate: Math.round(c.standardizedCostsRate * 100) }),
            dataIndex: 'standardizedCosts',
            align: 'right',
            render: money,
          },
          { title: t('calc.fields.taxBase'), dataIndex: 'taxBase', align: 'right', render: money },
          {
            title: t('calc.fields.tax', { rate: Math.round(c.taxRate * 100) }),
            dataIndex: 'tax',
            align: 'right',
            render: (v) => <b>{money(v)}</b>,
          },
        ].filter(Boolean)}
        summary={() =>
          c.recipients.length > 1 ? (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0}>
                <b>{t('summary.total')}</b>
              </Table.Summary.Cell>
              {c.currency !== 'RSD' && <Table.Summary.Cell index={1} />}
              {['netRsd', 'gross', null, 'taxBase', 'tax'].map((k, i) => (
                <Table.Summary.Cell key={i} index={i + 2} align="right">
                  {k ? <b>{money(c.totals[k])}</b> : null}
                </Table.Summary.Cell>
              ))}
            </Table.Summary.Row>
          ) : null
        }
      />
      <p className="text-xs opacity-60">
        {t('calc.coefficientNote', { coefficient: c.coefficient })}
      </p>
    </>
  );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={880}
      destroyOnClose
      title={c ? `${kindLabel(c.kind)}: ${c.subject} · ${c.pid}` : t('calc.newTitle')}
      extra={c && <Tag color={isDone ? 'green' : 'default'}>{t(`calc.status.${c.status}`)}</Tag>}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {editable && (
          <Form form={form} layout="vertical" onFinish={(v) => save.mutate(v)}>
            <Space wrap align="start">
              <Form.Item name="kind" label={t('calc.fields.kind')} rules={[{ required: true }]}>
                <Select style={{ width: 170 }} options={CALC_KINDS.map((k) => ({ value: k, label: kindLabel(k) }))} />
              </Form.Item>
              <Form.Item
                name="subject"
                label={t('calc.fields.subject')}
                tooltip={t('calc.help.subject')}
                rules={[{ required: true, message: t('errors.calc_subject_required') }]}
              >
                <AutoComplete
                  style={{ width: 320 }}
                  options={subjects.map((s) => ({ value: s }))}
                  onSelect={pickSubject}
                  filterOption={(input, o) => o.value.toLowerCase().includes(input.toLowerCase())}
                />
              </Form.Item>
              <Form.Item name="project" label={t('fields.project')}>
                <Select
                  allowClear
                  style={{ width: 220 }}
                  options={projects.map((p) => ({ value: p._id, label: `${p.code} ${p.name}` }))}
                />
              </Form.Item>
            </Space>

            <Space wrap align="start">
              <Form.Item name="month" label={t('filters.month')} tooltip={t('calc.help.period')} rules={[{ required: true }]}>
                <Select style={{ width: 90 }} options={MONTHS.map((m) => ({ value: m, label: m }))} />
              </Form.Item>
              <Form.Item name="year" label={t('filters.year')} rules={[{ required: true }]}>
                <InputNumber style={{ width: 100 }} min={2000} max={2100} />
              </Form.Item>
              <Form.Item name="months" label={t('calc.fields.months')} tooltip={t('calc.help.months')} rules={[{ required: true }]}>
                <InputNumber style={{ width: 110 }} min={1} max={36} precision={0} />
              </Form.Item>
              <Form.Item name="currency" label={t('calc.fields.currency')}>
                <Select
                  style={{ width: 90 }}
                  options={['EUR', 'RSD'].map((v) => ({ value: v, label: v }))}
                  onChange={(v) => loadRate(v, form.getFieldValue('rateDate'))}
                />
              </Form.Item>
              {currency !== 'RSD' && (
                <>
                  <Form.Item name="rateDate" label={t('calc.fields.rateDate')} tooltip={t('calc.help.rate')}>
                    <DatePicker format="DD.MM.YYYY" onChange={(d) => loadRate(form.getFieldValue('currency'), d)} />
                  </Form.Item>
                  <Form.Item
                    name="exchangeRate"
                    label={t('calc.fields.exchangeRate')}
                    rules={[{ required: true, message: t('errors.calc_invalid_rate') }]}
                  >
                    <InputNumber style={{ width: 130 }} min={0} step={0.0001} decimalSeparator="," />
                  </Form.Item>
                </>
              )}
            </Space>

            <Divider orientation="left" plain>
              {t(kind === 'rent' ? 'calc.landlord' : 'calc.recipients')}
            </Divider>
            <Form.List name="recipients">
              {(fields, { add, remove: removeRow }) => (
                <>
                  {fields.map(({ key, name }) => (
                    <Space key={key} wrap align="start">
                      <Form.Item
                        name={[name, 'name']}
                        label={t('calc.fields.recipient')}
                        rules={[{ required: true, message: t('errors.calc_recipient_required') }]}
                      >
                        <Input style={{ width: 200 }} />
                      </Form.Item>
                      <Form.Item name={[name, 'idNumber']} label={t('calc.fields.idNumber')}>
                        <Input style={{ width: 150 }} />
                      </Form.Item>
                      {kind === 'rent' && (
                        <Form.Item name={[name, 'account']} label={t('calc.fields.account')}>
                          <Input style={{ width: 200 }} placeholder="160-0000000000000-00" />
                        </Form.Item>
                      )}
                      <Form.Item
                        name={[name, 'netPerMonth']}
                        label={t('calc.fields.netPerMonth', { currency: currency || 'EUR' })}
                        rules={[{ required: true, message: t('errors.calc_recipient_required') }]}
                      >
                        <InputNumber style={{ width: 140 }} min={0} step={0.01} decimalSeparator="," />
                      </Form.Item>
                      {fields.length > 1 && (
                        <Button
                          shape="round"
                          type="text"
                          icon={<MinusCircleOutlined />}
                          style={{ marginTop: 30 }}
                          onClick={() => removeRow(name)}
                        />
                      )}
                    </Space>
                  ))}
                  {kind !== 'rent' && (
                    <Button shape="round" icon={<PlusOutlined />} onClick={() => add({})} style={{ marginBottom: 16 }}>
                      {t('calc.addRecipient')}
                    </Button>
                  )}
                </>
              )}
            </Form.List>

            <Space wrap align="start">
              <Form.Item name="payerAccount" label={t('calc.fields.payerAccount')}>
                <AutoComplete
                  style={{ width: 260 }}
                  options={payerAccounts.map((a) => ({ value: a.value, label: a.label }))}
                  placeholder="170-0000000000000-00"
                />
              </Form.Item>
              {kind === 'rent' && (
                <Form.Item name="netPurpose" label={t('calc.fields.netPurpose')} tooltip={t('calc.help.netPurpose')}>
                  <Input style={{ width: 240 }} />
                </Form.Item>
              )}
            </Space>
            <Form.Item name="note" label={t('fields.note')}>
              <Input.TextArea autoSize={{ minRows: 1, maxRows: 4 }} />
            </Form.Item>

            {save.isError && <Alert type="error" showIcon message={errorText(t, save.error)} style={{ marginBottom: 12 }} />}
            <Space>
              <Button shape="round" type="primary" htmlType="submit" loading={save.isPending}>
                {t(currentId ? 'calc.saveChanges' : 'calc.calculate')}
              </Button>
              {currentId && (
                <Popconfirm title={t('calc.deleteConfirm')} onConfirm={() => remove.mutate()}>
                  <Button shape="round" danger icon={<DeleteOutlined />} loading={remove.isPending}>
                    {t('calc.delete')}
                  </Button>
                </Popconfirm>
              )}
            </Space>
          </Form>
        )}

        {results && (
          <>
            {editable && (
              <Divider orientation="left" plain>
                {t('calc.result')}
              </Divider>
            )}
            {results}
          </>
        )}

        {c && (
          <>
            <Divider orientation="left" plain>
              {t('calc.returnData')}
            </Divider>
            {editable ? (
              <Form form={doneForm} layout="vertical" onFinish={(v) => done.mutate({ ...v, done: true })}>
                <p className="mb-2 text-sm opacity-70">{t('calc.help.done')}</p>
                <Space wrap align="start">
                  <Form.Item name="bop" label={t('fields.bop')} rules={[{ required: true, message: t('errors.calc_bop_required') }]}>
                    <Input style={{ width: 230 }} maxLength={25} />
                  </Form.Item>
                  <Form.Item
                    name="returnNumber"
                    label={t('calc.fields.returnNumber')}
                    rules={[{ required: true, message: t('errors.calc_uid_required') }]}
                  >
                    <Input style={{ width: 230 }} />
                  </Form.Item>
                  <Button shape="round" type="primary" htmlType="submit" loading={done.isPending} style={{ marginTop: 30 }}>
                    {t('calc.markDone')}
                  </Button>
                </Space>
                {done.isError && <Alert type="error" showIcon message={errorText(t, done.error)} />}
              </Form>
            ) : (
              <Descriptions size="small" column={2} bordered>
                <Descriptions.Item label={t('fields.bop')}>{c.bop || '—'}</Descriptions.Item>
                <Descriptions.Item label={t('calc.fields.returnNumber')}>{c.returnNumber || '—'}</Descriptions.Item>
                <Descriptions.Item label={t('calc.fields.return')} span={2}>
                  {c.taxReturn ? (
                    <Space>
                      <Tag color={STATUS_COLORS[c.taxReturn.status]}>{statusLabel(c.taxReturn.status)}</Tag>
                      <span className="text-xs opacity-70">
                        {t('fields.dueDate')}: {date(c.taxReturn.dueDate)}
                      </span>
                      {onOpenReturn && (
                        <Button shape="round" size="small" onClick={() => onOpenReturn(c.taxReturn._id)}>
                          {t('calc.openReturn')}
                        </Button>
                      )}
                    </Space>
                  ) : (
                    <span className="text-xs opacity-70">{t('calc.noReturnYet')}</span>
                  )}
                </Descriptions.Item>
              </Descriptions>
            )}
          </>
        )}

        {isDone && c.orders.length > 0 && (
          <>
            <Divider orientation="left" plain>
              {t('calc.orders')}
            </Divider>
            <Table
              size="small"
              rowKey={(o, i) => `${o.kind}-${i}`}
              pagination={false}
              dataSource={c.orders}
              columns={[
                { title: t('calc.fields.orderKind'), dataIndex: 'kind', render: (k) => t(`calc.orderKinds.${k}`) },
                { title: t('slip.payee'), dataIndex: 'payee' },
                { title: t('fields.paymentAccount'), dataIndex: 'account' },
                { title: t('calc.fields.purposeText'), dataIndex: 'purpose' },
                { title: t('fields.amount'), dataIndex: 'amount', align: 'right', render: money },
                {
                  title: '',
                  width: 120,
                  render: (o) =>
                    o.qr ? (
                      <Button
                        shape="round"
                        size="small"
                        icon={<QrcodeOutlined />}
                        onClick={() =>
                          onSlip?.({
                            title: `${t(`calc.orderKinds.${o.kind}`)}: ${c.subject}`,
                            slip: { ...o, dueDate: c.taxReturn?.dueDate || null },
                          })
                        }
                      >
                        {t('slip.button')}
                      </Button>
                    ) : null,
                },
              ]}
            />
          </>
        )}

        {isDone && canManage && (
          <Popconfirm title={t('calc.reopenConfirm')} onConfirm={() => done.mutate({ done: false })}>
            <Button shape="round">{t('calc.reopen')}</Button>
          </Popconfirm>
        )}
      </Space>
    </Drawer>
  );
}
